import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { styled } from '@mui/material/styles';

import {
  backdropBox,
  backdropFor,
  GROUND,
  inkAt,
  inkProgress,
  type Backdrop,
  type BackdropBox,
} from './backdrop';

/**
 * The full-bleed page the account pages and a shared schedule are drawn on: a
 * photograph placed against Sam's anchor line, a flat green below it, and a
 * header whose ink crosses from off-black to parchment as the green comes up
 * behind it.
 *
 * The picture is a layer inside the scroller, not `position: fixed`, so the
 * page scrolls out from under it and the flat colour simply carries on: that is
 * what makes a shared schedule scroll for ever without a seam.
 *
 * Everything the picture's placement depends on — the drawn height, the
 * narrow-window upscale, the anchor — is worked out here in JavaScript and
 * published as CSS custom properties, rather than left as `calc(…vh …vw)` in
 * each page's stylesheet. Two reasons: CSS cannot divide one length by another,
 * which the upscale needs, and `100vh` is the *large* viewport on iOS while the
 * picture is laid out against the visible one. Published together, the card and
 * the grass it sits on can never disagree.
 */

/* ── Parts ──────────────────────────────────────────────────────────────── */

const Page = styled('div')({
  position: 'fixed',
  inset: 0,
  /**
   * Over the app, which stays mounted underneath on `/account` (AppShell).
   * Nothing above `#root` makes a stacking context, so without this App's own
   * positioned layers paint straight through the photograph. The view toggle
   * is the one thing meant to sit above it, at 1270.
   */
  zIndex: 1260,
  overflowY: 'auto',
  // The picture is drawn one viewport wide, scrollbar included, so it can
  // overhang by the scrollbar's width. It is centred; let it crop.
  overflowX: 'hidden',
  overscrollBehavior: 'contain',
  background: GROUND,
  color: '#362f2a',
});

/**
 * The photograph, painted as this layer's background rather than drawn as a
 * box of its own.
 *
 * A 2,054px-tall absolutely-positioned box would extend the page's scrollable
 * area to its own bottom, and the laptop's account page — which Sam says must
 * not scroll at all when everything fits — would scroll to the end of the
 * picture. As a background it is clipped to the layer, the layer is exactly as
 * tall as the page's content, and past the picture's bottom edge the Page's
 * own flat `GROUND` simply carries on.
 */
const Picture = styled('div')({
  position: 'absolute',
  inset: 0,
  // Behind the page's own content: a positioned layer with `z-index: auto`
  // paints over in-flow boxes, photograph and all.
  zIndex: 0,
  backgroundRepeat: 'no-repeat',
  pointerEvents: 'none',
});

/**
 * Over the picture rather than in the flow, so that scrolling carries the
 * gradient up behind it — which is the whole reason its ink moves.
 */
const Top = styled('header')({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 2,
  color: 'var(--header-ink)',
  transition: 'none',
});

const Body = styled('div')({
  position: 'relative',
  // At least a screenful, so a short page still has picture under it.
  minHeight: '100%',
});

/** The page itself, over the picture. */
const Content = styled('div')({
  position: 'relative',
  zIndex: 1,
});

/* ── The page ───────────────────────────────────────────────────────────── */

interface Frame {
  backdrop: Backdrop;
  box: BackdropBox;
}

const measure = (): Frame => {
  // `window.innerWidth`, not `clientWidth`: it is what CSS `100vw` means, and
  // the two must agree or the picture and the cards on it drift apart.
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const backdrop = backdropFor(vw);
  return { backdrop, box: backdropBox(backdrop, vw, vh) };
};

interface Props {
  /** The lockup and links across the top. Drawn in `--header-ink`. */
  header?: ReactNode;
  children: ReactNode;
  /** Extra room at the foot, for the phone's view toggle. */
  bottom?: string | number;
  className?: string;
}

export default function BackdropPage({ header, children, bottom, className }: Props) {
  const page = useRef<HTMLDivElement | null>(null);
  const top = useRef<HTMLElement | null>(null);
  const [frame, setFrame] = useState<Frame | null>(null);
  /** The vertical middle of the header's contents: where the ink is judged. */
  const headerMid = useRef(40);

  const paint = useCallback(() => {
    const root = page.current;
    if (!root || !frame) return;
    const progress = inkProgress(frame.backdrop, frame.box, headerMid.current, root.scrollTop);
    root.style.setProperty('--header-ink', inkAt(progress));
  }, [frame]);

  // The picture depends on both viewport axes, so a resize redraws it.
  useLayoutEffect(() => {
    const remeasure = () => {
      headerMid.current = (top.current?.offsetHeight ?? 80) / 2;
      setFrame(measure());
    };
    remeasure();
    window.addEventListener('resize', remeasure);
    window.addEventListener('orientationchange', remeasure);
    /**
     * And the page's own box, which catches what a `resize` event misses: a
     * phone's URL bar sliding away, a desktop window's scrollbar appearing,
     * and the header settling to its real height once its font has loaded.
     */
    const observer = new ResizeObserver(remeasure);
    if (page.current) observer.observe(page.current);
    if (top.current) observer.observe(top.current);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', remeasure);
      window.removeEventListener('orientationchange', remeasure);
    };
  }, []);

  /**
   * One custom-property write per frame and no React state, so a long scroll
   * repaints the header's four words and nothing else. `animation-timeline:
   * scroll()` would say this in three lines and not run on an iPhone.
   */
  useEffect(() => {
    const root = page.current;
    if (!root) return;
    paint();
    let queued = 0;
    const onScroll = () => {
      if (queued) return;
      queued = requestAnimationFrame(() => {
        queued = 0;
        paint();
      });
    };
    root.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      root.removeEventListener('scroll', onScroll);
      if (queued) cancelAnimationFrame(queued);
    };
  }, [paint]);

  const vars = frame
    ? ({
        '--backdrop-anchor': `${frame.box.anchor}px`,
        '--first-top': `${frame.box.firstTop}px`,
      } as CSSProperties)
    : undefined;

  return (
    <Page ref={page} className={className} style={{ ...vars, paddingBottom: bottom }}>
      <Body>
        {frame && (
          <Picture
            aria-hidden="true"
            style={{
              backgroundImage: `url(${frame.backdrop.src})`,
              backgroundSize: `${frame.box.width}px ${frame.box.height}px`,
              // Centred across, so a picture wider than the window crops evenly.
              backgroundPosition: `center ${frame.box.top}px`,
            }}
          />
        )}
        <Content>{children}</Content>
      </Body>
      {header && <Top ref={top}>{header}</Top>}
    </Page>
  );
}
